// Estado do cliente sentado na mesa: qual mesa, quem é ele, se já foi
// liberado pelo anfitrião e o que a mesa já pediu.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { guardarPulseira } from "@/features/cliente-sessao";

import * as api from "./api";
import { carregar, limpar, salvar } from "./sessaoMesa";
import { useBloqueioSeLibera } from "./useBloqueioSeLibera";
import { useOutraMesa } from "./useOutraMesa";
import { useParticipantes } from "./useParticipantes";

const RECARREGA_COMANDA_MS = 8000;
const TELAS_DE_QUEM_PEDE = ["cardapio", "conferencia", "comanda"];

// Tela pra onde a sessão guardada leva: sem identidade pede o CPF; liberado
// vai pro cardápio; pendente ou recusado espera o anfitrião.
const telaDaSessao = (s) => {
  if (!s?.cliente || !s?.chave) return "identificacao";
  return s.eu?.status === "aprovado" ? "cardapio" : "aguardando";
};

// O link de convite traz o código na URL: /#/mesa3/<token>?convite=<código>.
// Quem entra por ele já chega liberado (o código muda a cada abertura da mesa).
const conviteDaUrl = () => {
  try {
    const hash = window.location.hash || "";
    const i = hash.indexOf("?");
    return i >= 0 ? new URLSearchParams(hash.slice(i + 1)).get("convite") : null;
  } catch {
    return null;
  }
};

// Comprovante da pessoa (link com o QR de saída). Leva o caminho de volta pra
// mesa, pra quem ainda vai pedir mais depois de pagar.
export const abrirComprovante = (codigo, { substituir = false } = {}) => {
  if (!codigo) return;
  const volta = (window.location.hash || "").replace(/^#/, "").split("?")[0];
  const destino = `#/saida/qr/${codigo}?voltar=${encodeURIComponent(volta)}`;
  // Ida automática (a pessoa já saiu): substitui a mesa no histórico, senão o
  // Voltar do navegador cai na mesa, que manda de novo para o comprovante.
  if (substituir) window.location.replace(destino);
  else window.location.hash = destino;
};

// Mesmo endereço da mesa em que o anfitrião está, com o código do convite.
const montarLinkConvite = (codigo) => {
  if (!codigo) return null;
  const hash = (window.location.hash || "").split("?")[0];
  return `${window.location.origin}${window.location.pathname}${hash}?convite=${codigo}`;
};

export function useMesaQr(token) {
  const [carregando, setCarregando] = useState(!!token);
  const [mesa, setMesa] = useState(null);
  const [config, setConfig] = useState(null);
  const [impedimento, setImpedimento] = useState(null);
  const [anfitriaoAberto, setAnfitriaoAberto] = useState(null);
  const [convite] = useState(() => (token ? conviteDaUrl() : null));
  const [conviteValido, setConviteValido] = useState(false);
  const [sessao, setSessao] = useState(() => (token ? carregar(token) : null));
  const [tela, setTela] = useState(token ? "entrada" : null);
  const [comanda, setComanda] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const ativo = !!token;
  const cliente = sessao?.cliente || null;
  const eu = sessao?.eu || null;
  const quem = useMemo(
    () => (sessao?.chave && cliente?.id
      ? { opened_at: sessao.opened_at, cliente_id: cliente.id, chave: sessao.chave }
      : null),
    [sessao?.opened_at, sessao?.chave, cliente?.id],
  );

  const guardar = useCallback((nova) => {
    salvar(token, nova);
    setSessao(nova);
  }, [token]);

  const expirou = useCallback((erro) => {
    limpar();
    setSessao(null);
    setComanda(null);
    setTela("entrada");
    if (erro) setImpedimento(erro);
  }, []);

  // Mudança de mesa (02/10/2026): a sessão guardada pode ser de outra mesa, e
  // a mesa deste celular pode mudar no meio do uso — ver useOutraMesa.
  const outraMesa = useOutraMesa(token);
  const seguirMesa = outraMesa.seguir;
  const conferirOutraMesa = outraMesa.conferir;
  const definirOutraMesa = outraMesa.definir;
  const mudarOutraMesa = outraMesa.mudar;
  const quemAgora = useRef(quem);
  quemAgora.current = quem;

  // "Sessão expirada" também é o que o celular ouve quando a mesa MUDOU (quem
  // abriu mudou, ou o garçom transferiu): antes de mandar o cliente escanear
  // de novo, vai atrás dela.
  const seguirOuExpirar = useCallback(async (erro) => {
    if (await seguirMesa(quemAgora.current)) return;
    expirou(erro);
  }, [seguirMesa, expirou]);

  // Trata os erros que mudam de tela, venham de onde vierem.
  const tratarErro = useCallback((erro) => {
    const codigo = erro?.codigo;
    if (codigo === "SESSAO_EXPIRADA") seguirOuExpirar(erro);
    else if (codigo === "PARTICIPANTE_INVALIDO") {
      setSessao((s) => {
        const n = s ? { ...s, cliente: null, chave: null, eu: null } : s;
        if (n) salvar(token, n);
        return n;
      });
      setTela("identificacao");
    } else if (codigo === "AGUARDANDO_LIBERACAO" || codigo === "ENTRADA_RECUSADA") {
      setTela("aguardando");
    } else if (codigo === "JA_SAIU") {
      setSessao((s) => {
        if (s?.eu?.codigo_saida) abrirComprovante(s.eu.codigo_saida, { substituir: true });
        return s;
      });
    }
  }, [seguirOuExpirar, token]);

  // Cada consulta da lista traz o "eu" atualizado: é assim que quem espera
  // descobre que foi liberado (ou recusado).
  const aoConsultar = useCallback((r) => {
    if (!r.ok) {
      tratarErro(r.erro);
      return;
    }
    const novo = r.dados?.eu;
    if (!novo) return;
    if (novo.saiu && novo.codigo_saida) {
      // Deram a saída desta pessoa: o celular vira o comprovante dela.
      abrirComprovante(novo.codigo_saida, { substituir: true });
      return;
    }
    setSessao((s) => {
      if (!s || (s.eu?.status === novo.status && s.eu?.papel === novo.papel && s.eu?.codigo_saida === novo.codigo_saida)) return s;
      const n = { ...s, eu: { status: novo.status, papel: novo.papel, codigo_saida: novo.codigo_saida } };
      salvar(token, n);
      return n;
    });
    if (novo.status === "aprovado") setTela((t) => (t === "aguardando" ? "cardapio" : t));
    else setTela((t) => (TELAS_DE_QUEM_PEDE.includes(t) ? "aguardando" : t));
  }, [tratarErro, token]);

  const ritmo = !quem ? null
    : eu?.status !== "aprovado" ? "esperando"
      : eu?.papel === "anfitriao" ? "anfitriao" : "normal";
  const participantes = useParticipantes({ token, quem, ritmo, onResposta: aoConsultar });
  const setParticipantes = participantes.setDados;

  // Decide a tela pelo que a mesa responde AGORA: é o que uma leitura nova do
  // QR mostraria. Devolve "indo" quando o celular está saindo desta mesa (foi
  // atrás da dele, ou a página trocou no meio da conferência).
  const aplicarEstado = useCallback(async (dados, vivo) => {
    // Sessão local só vale se for a mesma que está aberta na mesa agora, e
    // só se nada estiver impedindo o pedido.
    const local = carregar(token);
    const mesmaSessao = !dados.impedimento && dados.mesa
      && local && local.opened_at && local.opened_at === dados.mesa.opened_at;
    // As perguntas ao servidor vêm ANTES de mexer na tela: quando é o aviso de
    // bloqueio que está se liberando, tirar o aviso primeiro desligaria a
    // conferência no meio (e mostraria a entrada errada por um instante).
    let outra = null;
    if (!mesmaSessao && dados.mesa) {
      // A sessão guardada é DESTA mesa, mas não é mais a dela: a mesa pode
      // ter mudado (quem recarrega a página antiga vai atrás dela).
      if (local?.cliente?.id && local.chave && local.opened_at) {
        const foi = await seguirMesa({ opened_at: local.opened_at, cliente_id: local.cliente.id, chave: local.chave });
        if (!vivo() || foi === "indo") return "indo";
      }
      // O celular guarda a sessão de OUTRA mesa: ou a mesa dele mudou para
      // cá (entra direto), ou ele leu o QR de uma mesa que não é a dele.
      outra = await conferirOutraMesa();
      if (!vivo()) return "indo";
    }
    setMesa(dados.mesa || null);
    setConfig(dados.config);
    setImpedimento(dados.impedimento || null);
    setAnfitriaoAberto(dados.anfitriao || null);
    setConviteValido(!!dados.convite_valido);
    if (mesmaSessao) {
      setSessao(local);
      // Quem estava esperando e agora abriu o link de convite passa de
      // novo pelo CPF — é o CPF + convite que libera a entrada.
      const esperando = local.cliente && local.eu?.status !== "aprovado";
      setTela(dados.convite_valido && esperando ? "identificacao" : telaDaSessao(local));
    } else if (outra?.sessao && !dados.impedimento) {
      guardar(outra.sessao);
      setTela(telaDaSessao(outra.sessao));
    } else if (outra?.outra && !dados.impedimento && !dados.mesa.opened_at) {
      setTela("outra_mesa"); // mesa vazia: mudar para cá ou voltar
    }
    return null;
  }, [token, conferirOutraMesa, seguirMesa, guardar]);

  useEffect(() => {
    if (!token) return undefined;
    let vivo = true;
    (async () => {
      const r = await api.estadoDaMesa(token, convite);
      if (!vivo) return;
      if (!r.ok) {
        setCarregando(false);
        setImpedimento(r.erro);
        return;
      }
      if ((await aplicarEstado(r.dados, () => vivo)) === "indo") return;
      setCarregando(false);
    })();
    return () => { vivo = false; };
  }, [token, convite, aplicarEstado]);

  // Com o aviso de bloqueio na tela, o celular pergunta de novo sozinho (ver
  // useBloqueioSeLibera) e só troca a tela quando a resposta muda: o motivo
  // acabou ou virou outro. Sem rede, fica como está e tenta na próxima.
  const codigoDoBloqueio = impedimento?.codigo || null;
  const reconferir = useCallback(async (vivo) => {
    const r = await api.estadoDaMesa(token, convite);
    if (!vivo() || !r.ok) return;
    if ((r.dados.impedimento?.codigo || null) === codigoDoBloqueio) return;
    await aplicarEstado(r.dados, vivo);
  }, [token, convite, codigoDoBloqueio, aplicarEstado]);

  useBloqueioSeLibera({
    // QR trocado pela loja não se resolve esperando: só com o cartão novo.
    ativo: ativo && !carregando && tela === "entrada" && !!codigoDoBloqueio && codigoDoBloqueio !== "QR_INVALIDO",
    conferir: reconferir,
  });

  const recarregarComanda = useCallback(async () => {
    if (!token || !quem) return;
    const r = await api.buscarComanda(token, quem);
    if (r.ok) {
      setComanda(r.dados.comanda);
      setMesa(r.dados.mesa);
    } else {
      tratarErro(r.erro);
    }
  }, [token, quem, tratarErro]);

  useEffect(() => {
    if (tela !== "comanda") return undefined;
    recarregarComanda();
    const id = setInterval(recarregarComanda, RECARREGA_COMANDA_MS);
    return () => clearInterval(id);
  }, [tela, recarregarComanda]);

  const entrar = useCallback(async () => {
    const r = await api.entrarNaMesa(token);
    if (!r.ok) {
      setImpedimento(r.erro);
      return false;
    }
    const nova = { token, opened_at: r.dados.mesa.opened_at, mesa: r.dados.mesa, cliente: null, chave: null, eu: null };
    const local = carregar(token);
    if (local && local.opened_at === nova.opened_at && local.cliente && local.chave) {
      Object.assign(nova, { cliente: local.cliente, chave: local.chave, eu: local.eu });
    }
    guardar(nova);
    setMesa(r.dados.mesa);
    setConfig(r.dados.config);
    setImpedimento(null);
    setTela(telaDaSessao(nova));
    return true;
  }, [token, guardar]);

  const identificar = useCallback(async (cpf, nome, telefone) => {
    const r = await api.identificarPorCpf(token, {
      opened_at: sessao?.opened_at, cpf, nome, telefone, convite: conviteValido ? convite : undefined,
    });
    if (!r.ok) {
      if (r.erro?.codigo === "SESSAO_EXPIRADA") expirou(r.erro);
      // Quem já está em outra mesa não abre esta: a resposta traz a mesa dele
      if (r.erro?.codigo === "EM_OUTRA_MESA") {
        definirOutraMesa(r.erro);
        setTela("outra_mesa");
      }
      return r.erro;
    }
    const d = r.dados;
    // Pulseira do cliente (01/11/2026): o CPF na mesa é a credencial dele; o
    // "Meu cartão" (fidelidade) e as rotas do cliente exigem a pulseira.
    guardarPulseira(d.cliente_token);
    const nova = {
      ...(sessao || { token }),
      opened_at: d.opened_at || sessao?.opened_at,
      cliente: d.cliente,
      chave: d.chave,
      eu: { status: d.eu?.status, papel: d.eu?.papel, codigo_saida: d.eu?.codigo_saida },
    };
    guardar(nova);
    setParticipantes({ eu: d.eu, anfitriao: d.anfitriao, participantes: d.participantes, pendentes: d.pendentes });
    setTela(telaDaSessao(nova));
    return { ok: true, novo: d.novo, cliente: d.cliente, eu: d.eu };
  }, [token, sessao, guardar, expirou, setParticipantes, convite, conviteValido, definirOutraMesa]);

  // Quem abriu a mesa confirma: a conta inteira (e quem está com ele) vem
  // para a mesa deste QR.
  const mudarParaCa = useCallback(async () => {
    const r = await mudarOutraMesa();
    if (!r.ok) return r;
    const d = r.dados;
    guardar(r.sessao);
    setMesa(d.mesa);
    setImpedimento(null);
    setParticipantes({
      eu: d.eu, anfitriao: d.anfitriao, participantes: d.participantes, pendentes: d.pendentes, convite: d.convite,
    });
    setTela("cardapio");
    return r;
  }, [mudarOutraMesa, guardar, setParticipantes]);

  const enviarRodada = useCallback(async (itens, observacao) => {
    if (!quem) return { codigo: "PARTICIPANTE_INVALIDO", mensagem: "Identifique-se de novo com o seu CPF." };
    setEnviando(true);
    const r = await api.enviarRodada(token, {
      opened_at: quem.opened_at,
      cliente_id: quem.cliente_id,
      chave: quem.chave,
      cliente_nome: cliente?.nome || null,
      observacao: observacao || null,
      items: itens,
    });
    setEnviando(false);
    if (!r.ok) {
      tratarErro(r.erro);
      return r.erro;
    }
    setComanda(r.dados.comanda);
    setTela("comanda");
    return { ok: true, pedido: r.dados.pedido };
  }, [token, quem, cliente, tratarErro]);

  const chamarGarcom = useCallback(async () => {
    const r = await api.chamarGarcom(token, sessao?.opened_at, cliente?.nome || "");
    if (r.ok) setMesa((m) => (m ? { ...m, chamado_aberto: true } : m));
    return r;
  }, [token, sessao, cliente]);

  return {
    ativo, carregando, mesa, config, impedimento, sessao, cliente, eu, comanda, tela, enviando,
    anfitriaoAberto, conviteValido, quem,
    codigoSaida: participantes.dados?.eu?.codigo_saida || sessao?.eu?.codigo_saida || null,
    participantes: participantes.dados,
    // Só o anfitrião recebe o código do convite (é ele quem decide quem entra).
    linkConvite: montarLinkConvite(participantes.dados?.convite),
    irPara: setTela,
    entrar, identificar, enviarRodada, chamarGarcom, recarregarComanda,
    decidirEntrada: participantes.decidir,
    pedirEntradaDeNovo: participantes.pedirDeNovo,
    // Mudança de mesa: a mesa em que o cliente já está (quando leu o QR de
    // outra) e o aviso de que a mesa dele mudou.
    minhaMesa: outraMesa.minhaMesa,
    mudando: outraMesa.mudando,
    mudarParaCa,
    voltarParaMinhaMesa: outraMesa.voltar,
    avisoMudanca: outraMesa.aviso,
    fecharAvisoMudanca: outraMesa.fecharAviso,
  };
}
